/**
 * event-service.js
 * Coordinates event prompt generation and follow-up responses.
 */

import { createGenerateEventPlan, createTeamContextLoader } from '../domain/event/generate-event-plan.js';
import { createSubmitEventFollowup } from '../domain/event/submit-event-followup.js';

const TEAM_TYPE_TO_LENS = {
  Scrum: 'scrum',
  Kanban: 'kanban',
  SAFe: 'safe',
  LeSS: 'less',
  XP: 'xp',
  Agile: 'agile',
};

export class EventService {
  constructor({
    promptBuilder,
    followupPromptBuilder,
    eventGateway,
    teamGateway = null,
  } = {}) {
    if (typeof promptBuilder !== 'function') {
      throw new Error('promptBuilder is required');
    }
    if (typeof followupPromptBuilder !== 'function') {
      throw new Error('followupPromptBuilder is required');
    }
    if (!eventGateway || typeof eventGateway.submitPlan !== 'function') {
      throw new Error('eventGateway with submitPlan() is required');
    }

    this.buildPlanPrompt = promptBuilder;
    this.buildFollowupPrompt = followupPromptBuilder;
    this.eventGateway = eventGateway;
    this.teamGateway = teamGateway;

    const teamCache = new Map();
    const fetchTeamDetails = this.teamGateway?.fetchDetails
      ? (teamId) => this.teamGateway.fetchDetails(teamId)
      : async () => null;

    this.generatePlanUseCase = createGenerateEventPlan({
      sendPlan: (payload) => this.eventGateway.submitPlan(payload),
      buildPlanPrompt: (...args) => this.buildPlanPrompt(...args),
      fetchTeamDetails,
      teamCache,
    });

    this.loadTeamContext = createTeamContextLoader(fetchTeamDetails, teamCache);

    this.submitFollowupUseCase = createSubmitEventFollowup({
      sendFollowup: (payload) => {
        if (typeof this.eventGateway.submitFollowup !== 'function') {
          throw new Error('eventGateway.submitFollowup is required');
        }
        return this.eventGateway.submitFollowup({
          persona: payload.persona,
          lens: payload.lens,
          teamId: payload.teamId,
          followupMessage: payload.followupMessage,
          originalMessage: payload.originalMessage,
          eventContext: payload.eventContext,
          attachments: payload.attachments,
        });
      },
      buildFollowupPrompt: (...args) => this.buildFollowupPrompt(...args),
      loadTeamContext: (teamId) => this.loadTeamContext(teamId),
      mapTeamTypeToLens,
    });
  }

  async generatePlan(request = {}) {
    const result = await this.generatePlanUseCase(request);
    return {
      response: result.response,
      precisionMessage: result.precisionMessage,
      context: result.context,
      tokens: result.tokens,
    };
  }

  async submitFollowup({
    question,
    eventContext = {},
    persona = 'Event Facilitator',
    lens = 'general',
    teamId = null,
    attachments = [],
    usePrecision = true,
  } = {}) {
    const result = await this.submitFollowupUseCase({
      question,
      eventContext,
      persona,
      lens,
      teamId,
      attachments,
      usePrecision,
    });

    return {
      response: result.response,
      precisionMessage: result.precisionMessage,
      context: result.context,
      tokens: result.tokens,
    };
  }
}

function mapTeamTypeToLens(teamType) {
  if (!teamType) {
    return null;
  }
  const mapped = TEAM_TYPE_TO_LENS[teamType];
  if (mapped) {
    return mapped;
  }
  return typeof teamType === 'string' ? teamType.toLowerCase() : null;
}

if (typeof window !== 'undefined') {
  window.AgileProServices = window.AgileProServices || {};
  window.AgileProServices.EventService = EventService;
}
